import { Alert, Button, Flex } from '@chakra-ui/react'
import { AlertTriangle, Plus, ExternalLink } from 'lucide-react'
import { useProperty, usePropertyStats } from '@/features/properties/hooks/useProperties'
import { useBudgets } from '../hooks'
import { BUDGET_TYPE_LABELS } from '../types'

interface ALURFundAlertProps {
  residenceId: string
  fiscalYearId?: string
  onCreateBudget?: () => void
}

/**
 * Composant d'alerte pour le fonds travaux ALUR obligatoire
 *
 * Règle ALUR (art. 14-2 loi 10/07/1965) :
 * - Résidences de 10 lots ou plus
 * - Ancienneté >= 5 ans (copropriétés immatriculées avant 2023)
 * - Ancienneté >= 10 ans (copropriétés immatriculées après 2023)
 * - Minimum 5% du budget prévisionnel charges courantes
 */
export function ALURFundAlert({ residenceId, fiscalYearId, onCreateBudget }: ALURFundAlertProps) {
  const { data: residence } = useProperty(residenceId)
  const { data: stats } = usePropertyStats(residenceId)
  const { data: budgets } = useBudgets(residenceId, fiscalYearId)

  // Vérifier si la résidence est éligible à l'obligation ALUR
  const isALUREligible = () => {
    if (!residence || !stats) return false

    // Condition 1: >= 10 lots
    if (stats.unitsCount < 10) return false

    // Condition 2: Ancienneté suffisante
    const constructionYear = residence.construction_year
    if (!constructionYear) return false

    const currentYear = new Date().getFullYear()
    const age = currentYear - constructionYear

    // Si construit avant 2023 : >= 5 ans d'ancienneté
    // Si construit après 2023 : >= 10 ans d'ancienneté
    if (constructionYear < 2023) {
      return age >= 5
    } else {
      return age >= 10
    }
  }

  // Vérifier si un budget charges courantes existe (peu importe le statut)
  const hasOperatingBudget = () => {
    if (!budgets) return false
    return budgets.some(b => b.budget_type === 'operating')
  }

  // Calculer les montants des budgets (tous statuts confondus pour le calcul)
  const getOperatingBudgetTotal = () => {
    if (!budgets) return 0
    return budgets
      .filter(b => b.budget_type === 'operating')
      .reduce((sum, b) => sum + (b.total_amount || 0), 0)
  }

  const getReserveBudgetTotal = () => {
    if (!budgets) return 0
    return budgets
      .filter(b => b.budget_type === 'reserve')
      .reduce((sum, b) => sum + (b.total_amount || 0), 0)
  }

  const hasReserveBudget = () => {
    if (!budgets) return false
    return budgets.some(b => b.budget_type === 'reserve')
  }

  // Ne pas afficher si pas éligible ALUR
  if (!isALUREligible()) return null

  // Ne pas afficher si pas de budget charges courantes
  // (on ne peut pas calculer le 5% sans référence)
  if (!hasOperatingBudget()) return null

  const operatingTotal = getOperatingBudgetTotal()
  const reserveTotal = getReserveBudgetTotal()
  const minimumRequired = operatingTotal * 0.05
  const hasReserve = hasReserveBudget()
  const isCompliant = hasReserve && reserveTotal >= minimumRequired

  // Si conforme, pas d'alerte
  if (isCompliant) return null

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value)
  }

  return (
    <Alert.Root status="warning" mb={4}>
      <Alert.Indicator>
        <AlertTriangle />
      </Alert.Indicator>
      <Alert.Content flex="1">
        <Alert.Title fontWeight="semibold">
          Fonds travaux ALUR obligatoire
        </Alert.Title>
        <Alert.Description>
          {!hasReserve ? (
            <>
              Cette résidence ({stats?.unitsCount} lots, construite en {residence?.construction_year})
              est soumise à l'obligation de constituer un fonds travaux ALUR (art. 14-2 loi du 10/07/1965).
              {operatingTotal > 0 && (
                <>
                  {' '}Le montant minimum recommandé est de <strong>{formatCurrency(minimumRequired)}</strong> (5% du budget {BUDGET_TYPE_LABELS.operating.toLowerCase()}).
                </>
              )}
            </>
          ) : (
            <>
              Le budget "{BUDGET_TYPE_LABELS.reserve}" actuel ({formatCurrency(reserveTotal)})
              est inférieur au minimum recommandé de <strong>{formatCurrency(minimumRequired)}</strong> (5% du budget charges courantes).
            </>
          )}
        </Alert.Description>
        <Flex mt={3} gap={2} flexWrap="wrap">
          {!hasReserve && onCreateBudget && (
            <Button
              size="sm"
              colorPalette="orange"
              variant="solid"
              onClick={onCreateBudget}
            >
              <Plus size={14} />
              Créer le budget fonds travaux
            </Button>
          )}
          <a
            href="https://www.legifrance.gouv.fr/loda/article_lc/LEGIARTI000043977665"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button size="sm" variant="ghost">
              <ExternalLink size={14} />
              En savoir plus
            </Button>
          </a>
        </Flex>
      </Alert.Content>
    </Alert.Root>
  )
}
